import { Skeleton, Tooltip } from "antd";
import { memo } from "react";
import { MessageEmpty } from "../../components/LoadingBox";
import S3Image from "../../components/S3Image";
import { openPage } from "../../util/library/Router";
import { PATHNAME, vesselType } from "../../util/varibles/constants";
import { FleetUtilizationItem, IVessel } from "../../util/varibles/interface";
import { normalizePercentages } from "./percentage";
import styles from "./style.module.scss";
import { useVesselOverview } from "./VesselOverviewContext";

interface IProps {
    data: IVessel
    segments?: FleetUtilizationItem
}

const SEGMENT_META = [
    { name: 'Operation', color: '#9fd4c4' },
    { name: 'Quarantine, Hygiene control', color: '#ef7f83' },
    { name: 'Maintenance', color: '#f1ad66' },
    { name: 'Idle time', color: '#b7b4aa' },
    { name: 'Travelling time', color: '#57a4d8' },
];

function Vessel({ data, segments }: IProps) {
    const { image_url, name } = data;
    const { operation, hygine, maintenance, idle, travelling } = segments || {}
    const [duration] = useVesselOverview(state => state.duration);
    const [loading] = useVesselOverview(state => state.loadingVesselIds.includes(data.id));
    const durationTime = duration[1] - duration[0];
    const segmentValues = [operation, hygine, maintenance, idle, travelling];
    const segmentPercentages = normalizePercentages(segmentValues.map(value =>
        value != null && Number.isFinite(value) && durationTime > 0
            ? value / durationTime * 100
            : 0
    ));
    const segmentsData = SEGMENT_META.map((segment, index) => ({
        ...segment,
        value: segmentValues[index],
        percentage: segmentPercentages[index],
    }));
    const hasNoActivityData = segmentValues
        .slice(0, 3)
        .every(value => value == null || value === 0);
    const cardClassName = [
        'relative w-full max-w-[495px] h-min overflow-hidden rounded-[8px] border border-[--border1]',
        hasNoActivityData && 'bg-[--bgSection]',
    ].filter(Boolean).join(' ');
    const progressClassName = [
        'flex h-[6px] w-full overflow-hidden',
        hasNoActivityData ? 'bg-transparent' : 'bg-[#e9ebed]',
    ].join(' ');

    const formatPercentage = (percentage?: number) => {
        if (percentage == null || !Number.isFinite(percentage) || durationTime <= 0) {
            return '--';
        }

        return `${Number.isInteger(percentage) ? percentage : percentage.toFixed(2)}%`;
    };
    const getSegmentWidth = (percentage?: number) => {
        if (percentage == null || !Number.isFinite(percentage) || durationTime <= 0) {
            return 0;
        }

        return Math.min(100, Math.max(0, percentage));
    };

    return <div className={cardClassName}>
        {/* Progress bar */}
        <div className={progressClassName}>
            {!hasNoActivityData && segmentsData.map(({ name: segmentName, color, percentage }) => (
                <Tooltip key={segmentName} title={`${segmentName}: ${formatPercentage(percentage)}`}>
                    <div
                        className="h-full shrink-0"
                        style={{
                            width: `${getSegmentWidth(percentage)}%`,
                            backgroundColor: color,
                        }}
                    />
                </Tooltip>
            ))}
        </div>
        {loading
            ? <div className="grid grid-cols-[.45fr_.55fr] gap-[12px] overflow-hidden py-[11px] px-[12px]">
                <div className="h-[150px] w-full overflow-hidden rounded-[8px]">
                    <Skeleton.Image
                        active
                        className="!w-full"
                        style={{ width: '100%', height: 160 }}
                    />
                </div>
                <div className="h-[150px] overflow-hidden pt-[2px]">
                    <Skeleton
                        active
                        className={styles['vessel-skeleton']}
                        title={{ width: '65%' }}
                        paragraph={{ rows: 5, width: ['90%', '100%', '72%', '58%', '80%'] }}
                    />
                </div>
            </div>
            : <div className="grid grid-cols-[.45fr_.55fr] gap-[12px] py-[11px] px-[12px]">
                <div className="h-[150px] overflow-hidden rounded-[8px]">
                    <S3Image imageUrl={image_url} />
                </div>

                <div className="relative min-w-0 text-[15px] leading-[22px] ">
                    <div className="mb-[4px] flex items-center gap-[6px] pr-[30px]">
                        <button
                            type="button"
                            className="truncate cursor-pointer border-0 bg-transparent p-0 text-left font-[600] uppercase text-[--colorLink] text-[15px]"
                            onClick={() => openPage(`${PATHNAME.VESSEL}/${data.id}`)}
                        >
                            {name}
                        </button>

                        <div className="text-comment italic text-[15px]">
                            - {vesselType[data.type]?.shortName}
                        </div>
                    </div>

                    {hasNoActivityData
                        ? <div className="h-[100px] flex items-center justify-center">
                            <MessageEmpty />
                        </div>
                        : <>
                            {segmentsData.map(({ name: segmentName, percentage }) => (
                                <div key={segmentName}>
                                    {segmentName} {formatPercentage(percentage)}
                                </div>
                            ))}
                        </>}
                </div>
            </div>}
    </div>
}

export default memo(Vessel);
